if (!customElements.get("size-guide-item")) {
  class SizeGuideItem extends HTMLElement {
    connectedCallback() {
      if (this.hasBoundToggle) return;
      this.hasBoundToggle = true;

      this.querySelectorAll("[data-size-unit-col]").forEach((cell) => {
        cell.dataset.original = cell.textContent.trim();
      });

      this.addEventListener("change", (event) => {
        const systemToggle = event.target.closest("[data-size-system-toggle]");
        if (!systemToggle || !this.contains(systemToggle)) return;

        this.toggleAttribute(
          `data-show-${systemToggle.dataset.sizeSystemToggle}`,
          systemToggle.checked,
        );
      });

      this.addEventListener("click", (event) => {
        const unitToggle = event.target.closest("[data-size-unit-toggle]");
        if (unitToggle && this.contains(unitToggle)) {
          this.setUnit(unitToggle.dataset.sizeUnitToggle);
        }
      });
    }

    setActive(attribute, value) {
      this.querySelectorAll(`[${attribute}]`).forEach((button) => {
        const isActive = button.getAttribute(attribute) === value;
        button.classList.toggle("size-guide__switch-option--active", isActive);
        button.setAttribute("aria-pressed", String(isActive));
      });
    }

    setUnit(unit) {
      if (this.dataset.sizeUnit === unit) return;

      this.dataset.sizeUnit = unit;
      this.setActive("data-size-unit-toggle", unit);

      const factor = unit === "cm" ? 2.54 : 1 / 2.54;

      this.querySelectorAll("[data-size-unit-col]").forEach((cell) => {
        const original = cell.dataset.original;

        if (cell.dataset.sizeUnitCol === unit) {
          cell.textContent = original;
        } else if (cell.tagName === "TH") {
          cell.textContent = original.replace(
            /\((?:in\s+)?(?:cm|inch(?:es)?|in)\)/i,
            `(${unit})`,
          );
        } else {
          cell.textContent = original.replace(/\d+(?:\.\d+)?/g, (number) =>
            (parseFloat(number) * factor).toFixed(1),
          );
        }
      });
    }
  }

  customElements.define("size-guide-item", SizeGuideItem);
}

if (!customElements.get("size-guide-picker")) {
  class SizeGuidePicker extends HTMLElement {
    connectedCallback() {
      this.bindTabs();

      if (!this.hasAttribute("data-selectable")) return;

      this.drawer = this.closest("side-drawer");
      this.onChange = this.handleChange.bind(this);
      this.onClick = this.handleClick.bind(this);
      this.onDrawerToggle = () => requestAnimationFrame(() => this.sync());
      this.onPageChange = this.handlePageChange.bind(this);

      this.addEventListener("change", this.onChange);
      this.addEventListener("click", this.onClick);
      this.drawer?.addEventListener("drawer-toggle", this.onDrawerToggle);
      document.addEventListener("change", this.onPageChange);

      this.buttonObserver = new MutationObserver(() => {
        if (this.isOpen) this.syncButtons();
      });
      this.observeBuyButtons();

      if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        this.cartAddUnsubscriber = subscribe(PUB_SUB_EVENTS.cartAdd, () => {
          if (!this.awaitingAdd) return;
          this.awaitingAdd = false;
          setTimeout(() => {
            if (this.isOpen) this.drawer.close();
          });
        });
      }

      this.sync();
    }

    disconnectedCallback() {
      if (!this.onChange) return;

      this.removeEventListener("change", this.onChange);
      this.removeEventListener("click", this.onClick);
      this.drawer?.removeEventListener("drawer-toggle", this.onDrawerToggle);
      document.removeEventListener("change", this.onPageChange);
      this.buttonObserver?.disconnect();
      this.cartAddUnsubscriber?.();
      this.onChange = null;
    }

    bindTabs() {
      if (this.hasBoundTabs) return;

      this.scroller = this.querySelector("[data-size-guide-scroll]");
      this.tabs = Array.from(this.querySelectorAll("[data-size-guide-tab]"));
      if (!this.scroller || !this.tabs.length) return;
      this.hasBoundTabs = true;

      this.tabs.forEach((tab) => {
        tab.addEventListener("click", () => {
          const panel = this.querySelector(
            `[data-size-guide-panel="${tab.dataset.sizeGuideTab}"]`,
          );
          if (!panel) return;

          this.setActiveTab(tab.dataset.sizeGuideTab);
          this.isTabScrolling = true;
          clearTimeout(this.tabScrollTimer);
          this.tabScrollTimer = setTimeout(() => {
            this.isTabScrolling = false;
          }, 600);

          const offset =
            panel.getBoundingClientRect().top -
            this.scroller.getBoundingClientRect().top +
            this.scroller.scrollTop;
          this.scroller.scrollTo({
            top: tab.dataset.sizeGuideTab === "chart" ? 0 : offset,
            behavior: "smooth",
          });
        });
      });

      this.scroller.addEventListener(
        "scroll",
        () => {
          if (!this.isTabScrolling) this.updateActiveTab();
        },
        { passive: true },
      );
    }

    setActiveTab(name) {
      this.tabs.forEach((tab) => {
        const isActive = tab.dataset.sizeGuideTab === name;
        tab.classList.toggle("size-guide-drawer__tab--active", isActive);
        tab.setAttribute("aria-pressed", String(isActive));
      });
    }

    updateActiveTab() {
      const measure = this.querySelector('[data-size-guide-panel="measure"]');
      if (!measure) return;

      const scroller = this.scroller;
      const atBottom =
        scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 2;
      const measureTop =
        measure.getBoundingClientRect().top - scroller.getBoundingClientRect().top;

      this.setActiveTab(
        atBottom || measureTop <= scroller.clientHeight / 3 ? "measure" : "chart",
      );
    }

    get isOpen() {
      return this.drawer?.hasAttribute("drawer-open");
    }

    get optionName() {
      return this.dataset.optionName;
    }

    get variantPicker() {
      const pickers = Array.from(
        document.querySelectorAll(
          `variant-radios[data-section="${CSS.escape(this.dataset.section || "")}"]`,
        ),
      ).filter((el) => !el.closest(".quick-add") && !el.closest("product-card"));

      return pickers[0] || null;
    }

    get pageInputs() {
      const picker = this.variantPicker;
      if (!picker) return [];

      return Array.from(picker.querySelectorAll("[data-input]")).filter(
        (input) => input.name === this.optionName,
      );
    }

    get radios() {
      return Array.from(this.querySelectorAll("[data-size-guide-radio]"));
    }

    get addButton() {
      return this.querySelector("[data-size-guide-add]");
    }

    get errorMessage() {
      return this.querySelector("[data-size-guide-error]");
    }

    get pageAddButton() {
      return document.querySelector(".buy-buttons-container [data-add-to-cart]");
    }

    normalize(value) {
      return String(value || "")
        .toLowerCase()
        .replace(/^\s*(uk\s*\/\s*india|uk|india)\s*/, "")
        .replace(/[^a-z0-9.]/g, "");
    }

    findPageInput(value) {
      const key = this.normalize(value);
      return this.pageInputs.find((input) => this.normalize(input.value) === key);
    }

    isPageInputUnavailable(input) {
      return (
        input.disabled ||
        input.closest("[data-variant-option]")?.classList.contains("radio--disabled")
      );
    }

    observeBuyButtons() {
      const container = document.querySelector(".buy-buttons-container");
      if (container) {
        this.buttonObserver.observe(container, {
          attributes: true,
          childList: true,
          characterData: true,
          subtree: true,
        });
      }
    }

    sync() {
      this.syncRadios();
      this.syncButtons();
    }

    sizeColumnIndex(table, keys) {
      const rows = Array.from(table.querySelectorAll("[data-size-guide-row]"));
      const cellCount = rows[0]?.cells.length || 0;
      let bestIndex = -1;
      let bestScore = 0;

      for (let index = 0; index < cellCount; index++) {
        if (rows[0].cells[index].classList.contains("size-guide__select-col")) continue;

        const score = rows.filter((row) =>
          keys.has(this.normalize(row.cells[index]?.textContent)),
        ).length;

        if (score > bestScore) {
          bestIndex = index;
          bestScore = score;
        }
      }

      return bestIndex;
    }

    resolveRadioValues(pageInputs) {
      const keys = new Set(pageInputs.map((input) => this.normalize(input.value)));

      this.querySelectorAll(".size-guide__table table").forEach((table) => {
        const index = this.sizeColumnIndex(table, keys);
        table.toggleAttribute("data-size-matched", index >= 0);
        if (index < 0) return;

        table.querySelectorAll("[data-size-guide-row]").forEach((row) => {
          const radio = row.querySelector("[data-size-guide-radio]");
          const value = row.cells[index]?.textContent.trim();
          if (!radio || !value) return;

          radio.value = value;
          radio.setAttribute("aria-label", value);
        });
      });
    }

    syncRadios() {
      const pageInputs = this.pageInputs;
      const checkedInput = pageInputs.find((input) => input.checked);
      const checkedKey = checkedInput ? this.normalize(checkedInput.value) : null;

      this.resolveRadioValues(pageInputs);

      this.radios.forEach((radio) => {
        const row = radio.closest("[data-size-guide-row]");
        const pageInput = this.findPageInput(radio.value);
        const offered = Boolean(pageInput) || !row?.closest("table[data-size-matched]");
        const unavailable = !pageInput || this.isPageInputUnavailable(pageInput);
        const selected =
          !unavailable && checkedKey !== null && this.normalize(radio.value) === checkedKey;

        radio.disabled = unavailable;
        radio.checked = selected;
        row?.classList.toggle("is-unavailable", unavailable);
        row?.classList.toggle("is-selected", selected);
        if (row) row.hidden = !offered;
      });

      if (checkedInput) this.toggleError(false);
    }

    syncButtons() {
      const addButton = this.addButton;
      const pageAddButton = this.pageAddButton;

      if (addButton) {
        addButton.hidden = !pageAddButton;

        if (pageAddButton) {
          const pageText = pageAddButton
            .querySelector("[data-add-to-cart-text], .button__text")
            ?.textContent.trim();
          const disabled =
            pageAddButton.hasAttribute("disabled") ||
            pageAddButton.getAttribute("aria-disabled") === "true";
          const text = addButton.querySelector("[data-size-guide-add-text]");

          if (pageText && text) text.textContent = pageText;
          addButton.disabled = disabled;
          addButton.setAttribute("aria-disabled", String(disabled));
        }
      }
    }

    toggleError(show) {
      if (this.errorMessage) this.errorMessage.hidden = !show;
    }

    handleChange(event) {
      const radio = event.target.closest("[data-size-guide-radio]");
      if (!radio) return;

      this.radios.forEach((item) => {
        item.closest("[data-size-guide-row]")?.classList.toggle("is-selected", item === radio);
      });
      this.toggleError(false);

      const pageInput = this.findPageInput(radio.value);
      if (!pageInput || pageInput.checked || this.isPageInputUnavailable(pageInput)) return;

      pageInput.checked = true;
      pageInput.dispatchEvent(new Event("change", { bubbles: true }));
    }

    handlePageChange(event) {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.name !== this.optionName) return;
      if (!this.variantPicker?.contains(input)) return;

      this.syncRadios();
    }

    handleClick(event) {
      if (event.target.closest("[data-size-guide-add]")) {
        this.addToCart();
        return;
      }

      const row = event.target.closest("[data-size-guide-row]");
      if (!row || event.target.closest("label, input")) return;

      const radio = row.querySelector("[data-size-guide-radio]");
      if (radio && !radio.disabled && !radio.checked) radio.click();
    }

    addToCart() {
      const pageAddButton = this.pageAddButton;
      if (!pageAddButton || this.addButton?.disabled) return;

      if (!this.radios.some((radio) => radio.checked)) {
        this.toggleError(true);
        return;
      }

      this.awaitingAdd = true;
      pageAddButton.click();
    }
  }

  customElements.define("size-guide-picker", SizeGuidePicker);
}